import {ColorsType, StyleType} from '../../../../utils/theming';
import CameraIcon from '../../Icons/icons/camera';
import CheckIcon from '../../Icons/icons/check';
import CloseIcon from '../../Icons/icons/close';

/*
 * ---------------
 * Colors:
 * The color, state, and style props will define the background, border, and text colors applied.
 * ----------------
 */
export const createColorMap = (Colors: ColorsType) => ({
  white: {
    enable: {
      backgroundColor: Colors.neutral100,
      iconColor: Colors.neutral1000,
      borderColor: Colors.primary700,
    },
    hover: {
      backgroundColor: Colors.neutral300,
      iconColor: Colors.neutral1000,
      borderColor: Colors.primary700,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  grey: {
    enable: {
      backgroundColor: Colors.neutral700,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.neutral900,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  purple: {
    enable: {
      backgroundColor: Colors.primary700,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.primary900,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  lightPurple: {
    enable: {
      backgroundColor: Colors.primary100,
      iconColor: Colors.primary700,
      borderColor: Colors.primary700,
    },
    hover: {
      backgroundColor: Colors.primary300,
      iconColor: Colors.primary700,
      borderColor: Colors.primary700,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  red: {
    enable: {
      backgroundColor: Colors.alert300,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.alert400,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  green: {
    enable: {
      backgroundColor: Colors.success300,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.success400,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  yellow: {
    enable: {
      backgroundColor: Colors.warning300,
      iconColor: Colors.neutral1000,
      borderColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.warning400,
      iconColor: Colors.neutral1000,
      borderColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  blue: {
    enable: {
      backgroundColor: Colors.info300,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.info400,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  teal: {
    enable: {
      backgroundColor: Colors.secondary600,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.secondary800,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  lightTeal: {
    enable: {
      backgroundColor: Colors.secondary200,
      iconColor: Colors.secondary600,
      borderColor: Colors.secondary600,
    },
    hover: {
      backgroundColor: Colors.secondary400,
      iconColor: Colors.secondary600,
      borderColor: Colors.secondary600,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  lightGrey: {
    enable: {
      backgroundColor: Colors.neutral500,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.neutral300,
      iconColor: Colors.neutral1000,
      borderColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  pink: {
    enable: {
      backgroundColor: Colors.tertiary600,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    hover: {
      backgroundColor: Colors.tertiary800,
      iconColor: Colors.neutral100,
      borderColor: Colors.neutral100,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
  lightPink: {
    enable: {
      backgroundColor: Colors.tertiary200,
      iconColor: Colors.tertiary600,
      borderColor: Colors.tertiary600,
    },
    hover: {
      backgroundColor: Colors.tertiary400,
      iconColor: Colors.tertiary600,
      borderColor: Colors.tertiary600,
    },
    disable: {
      backgroundColor: Colors.neutral400,
      iconColor: Colors.neutral500,
    },
  },
});

/* ButtonIcon container styles */
export const getButtonIconGeneralStyles = (
  colors: ColorsType,
  color: string,
  style: string,
  state: string,
  icon: any,
  size: string,
  showStroke?: boolean,
  onClick?: (x?: any) => void,
) => {
  const dynamicColorMap = createColorMap(colors);

  // @ts-ignore
  const {backgroundColor, iconColor, borderColor} =
    // @ts-ignore
    dynamicColorMap[color]?.[state] || {};

  const cursor = state !== 'disable' && !!onClick ? 'pointer' : 'auto';

  return {
    backgroundColor: backgroundColor || 'transparent',
    iconColor,
    cursor,
    borderRadius: style === 'square' ? 8 : 32,
    padding: paddingMap[size],
    height: getButtonIconMinSize(icon, size),
    width: getButtonIconMinSize(icon, size),
    border:
      showStroke && borderColor
        ? `${borderWidthMap[size]}px solid ${borderColor}`
        : 'none',
  };
};

/* ButtonIcon borderWidths */
export const borderWidthMap: Record<string, number> = {
  '2xlarge': 3,
  xlarge: 2.73,
  large: 2.18,
  medium: 2.18,
  small: 1.91,
  xsmall: 1.43,
  '2xsmall': 1.27,
  '3xsmall': 1.11,
};

/* ButtonIcon indicatorSize */
export const indicatorSizeMap: Record<string, string> = {
  '2xlarge': '16',
  xlarge: '14',
  large: '10',
  medium: '10',
  small: '8',
  xsmall: '5',
  '2xsmall': '5',
  '3xsmall': '4',
};

/* ButtonIcon paddings */
export const paddingMap: Record<string, number> = {
  '2xlarge': 10,
  xlarge: 12,
  large: 8,
  medium: 6,
  small: 6,
  xsmall: 4,
  '2xsmall': 4,
  '3xsmall': 4,
};

export const iconSizeMap: Record<string, any> = {
  '2xlarge': '24',
  xlarge: '16',
  large: '16',
  medium: '16',
  small: '12',
  xsmall: '12',
  '2xsmall': '8',
  '3xsmall': '6',
};

export const getIconSize = (icon: any, size: string) => {
  // TODO: set always iconSizeMap[size] when all icons have all sizes
  return icon === CheckIcon ||
    icon === CloseIcon ||
    icon === CameraIcon ||
    !size?.includes('small')
    ? iconSizeMap[size]
    : 16;
};

export const buttonIconSizeMap: Record<string, number> = {
  '2xlarge': 44,
  xlarge: 40,
  large: 32,
  medium: 28,
  small: 24,
  xsmall: 18,
  '2xsmall': 16,
  '3xsmall': 14,
};

export const getButtonIconMinSize = (icon: any, size: string) => {
  // TODO: set always buttonIconSizeMap[size] when all icons have all sizes
  return icon === CheckIcon ||
    icon === CloseIcon ||
    icon === CameraIcon ||
    !size?.includes('xsmall')
    ? buttonIconSizeMap[size]
    : 18;
};
export const getButtonIconContentStyles = (
  fontStyles: StyleType,
  colors: ColorsType,
  color: string,
  state: string,
) => {
  const dynamicColorMap = createColorMap(colors);

  const iconColor =
    // @ts-ignore
    dynamicColorMap[color]?.[state]?.iconColor;

  const buttonGeneralFontStyle = fontStyles['buttonSM'];

  const buttonFontStyle = {
    ...buttonGeneralFontStyle,
    lineHeight: buttonGeneralFontStyle?.lineHeight + 'px',
    color: iconColor,
  };

  return {styles: buttonFontStyle, iconColor};
};
